// community.ts
import { createClient } from "@supabase/supabase-js";

// Supabase 클라이언트 생성
const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL!;
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

// 게시글 타입 (DB 테이블과 1:1 매칭)
export interface Post {
  id: string;
  title: string;
  content: string;
  author_id: string;
  author_name?: string;
  category: string | null;
  views: number;
  likes: number;
  is_pinned: boolean;
  created_at: string;
  updated_at: string;
}

// insert 가능한 타입 정의
export type PostInsert = Omit<Post, "id" | "views" | "likes" | "created_at" | "updated_at" | "is_pinned" | "author_name" | "category"> & {
  category: string;
  is_pinned?: boolean;
};

// update 가능한 타입 정의
export type PostUpdate = Partial<Omit<Post, "id" | "author_id" | "created_at" | "updated_at">>;

/**
 * 게시글 등록
 */
export async function createPost(post: PostInsert): Promise<Post> {
  const { data, error } = await supabase
    .from("posts")
    .insert(post)
    .select()
    .single();

  if (error || !data) throw error;

  return data;
}

/**
 * 게시글 조회
 */
export async function getPost(id: string): Promise<Post> {
  // 1. 게시글 정보 가져오기
  const { data: postData, error: postError } = await supabase
    .from("posts")
    .select("*") // profiles 조인 제거
    .eq("id", id)
    .single();

  if (postError || !postData) throw postError || new Error("Post not found");

  let author_name: string | undefined = undefined;

  // 2. 작성자 정보 가져오기 (author_id가 있는 경우)
  if (postData.author_id) {
    const { data: profileData, error: profileError } = await supabase
      .from("profiles")
      .select("nickname")
      .eq("id", postData.author_id) // profiles 테이블의 id (uuid)와 비교
      .single();

    if (profileError) {
      // 프로필 조회 실패 시 오류를 로깅하고 진행할 수 있음
      console.error(`Error fetching profile for author ${postData.author_id}:`, profileError);
    } else if (profileData) {
      author_name = profileData.nickname;
    }
  }

  // 3. 데이터 조합하여 반환
  return { ...postData, author_name };
}

/**
 * 게시글 목록 조회
 */
export async function getPosts(options?: {
  category?: string;
  limit?: number;
  offset?: number;
}): Promise<Post[]> {
  // 1. 게시글 목록 가져오기
  let query = supabase
    .from("posts")
    .select("*") // profiles 조인 제거
    .order("is_pinned", { ascending: false })
    .order("created_at", { ascending: false });

  if (options?.category) {
    query = query.eq("category", options.category);
  }

  if (options?.limit) {
    query = query.limit(options.limit);
  }

  if (options?.offset !== undefined) {
    const limit = options.limit || 10;
    query = query.range(options.offset, options.offset + limit - 1);
  }

  const { data: postsData, error: postsError } = await query;

  if (postsError || !postsData) throw postsError || new Error("Failed to fetch posts");
  if (postsData.length === 0) return []; // 게시글 없으면 빈 배열 반환

  // 2. 작성자 ID 목록 추출 (null/undefined 제외, 중복 제거)
  const authorIdSet = new Set(postsData.map(p => p.author_id).filter(id => id)); 
  const authorIds = Array.from(authorIdSet);

  let profilesMap = new Map<string, string>(); // Map<author_id, nickname>

  // 3. 작성자 정보 가져오기 (authorIds가 있는 경우)
  if (authorIds.length > 0) {
    const { data: profilesData, error: profilesError } = await supabase
      .from("profiles")
      .select("id, nickname")
      .in("id", authorIds);

    if (profilesError) {
      console.error("Error fetching profiles:", profilesError);
      // 프로필 조회 실패 시에도 게시글 목록은 반환 (author_name 없이)
    } else if (profilesData) {
      // author_id를 키로 하는 닉네임 맵 생성
      profilesData.forEach(profile => {
        if (profile.id && profile.nickname) {
          profilesMap.set(profile.id, profile.nickname);
        }
      });
    }
  }

  // 4. 게시글 데이터에 author_name 추가
  const postsWithAuthorNames = postsData.map((post) => ({
    ...post,
    author_name: post.author_id ? profilesMap.get(post.author_id) : undefined,
  }));

  return postsWithAuthorNames;
}

/**
 * 게시글 수정
 */
export async function updatePost(id: string, updates: PostUpdate): Promise<Post> {
  const { data, error } = await supabase
    .from("posts")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error || !data) throw error;

  return data;
}

/**
 * 게시글 삭제
 */
export async function deletePost(id: string): Promise<void> {
  const { error } = await supabase
    .from("posts")
    .delete()
    .eq("id", id);

  if (error) throw error;
}

/**
 * 게시글 좋아요 증가
 */
export async function likePost(id: string): Promise<Post> {
  const { data, error } = await supabase.rpc('increment_likes', { post_id: id });

  if (error || !data) throw error;

  return data;
}
